/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@/shared/ui";
import { getUserAuthManager } from "@/context/authentication";
import { forgetClearance } from "@shared/utils/antibot-clearance-store";
import {
  SUPPORT_LANDING_PATH,
  buildSupportUser,
  decodeJwtPayload,
  parseSupportFragment,
  supportClaimsFrom,
  type SupportClaims,
} from "./support-session";

/** Reads and wipes the support link; `null` unless it holds a live support token. */
function readSupportLink(): { token: string; claims: SupportClaims } | null {
  const token = parseSupportFragment(window.location.hash);
  window.history.replaceState(null, document.title, window.location.pathname);
  if (!token) return null;
  const claims = supportClaimsFrom(decodeJwtPayload(token));
  if (!claims || claims.expiresAt * 1000 <= Date.now()) return null;
  return { token, claims };
}

/**
 * Installs the support token from the URL fragment as the current session.
 *
 * The fragment is wiped from the address bar first, so the token never
 * lingers in history. Returns `false` when the link carries no valid,
 * unexpired support token.
 */
export async function startSupportSession(): Promise<boolean> {
  const link = readSupportLink();
  if (!link) return false;

  // The anti-bot clearance is bound to whichever bearer was here before.
  forgetClearance();
  await getUserAuthManager()?.storeUser(buildSupportUser(link.token, link.claims));
  // Full navigation so the auth provider boots with the stored support user.
  window.location.replace(SUPPORT_LANDING_PATH);
  return true;
}

/** Landing page of the Sauron "acessar como cliente" link. */
export const SupportAccessPage: React.FC = () => {
  const { t } = useTranslation("features/support-access");
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    startSupportSession().then(
      (started) => setInvalid(!started),
      () => setInvalid(true),
    );
  }, []);

  return (
    <main className="flex min-h-screen flex-col items-center justify-center gap-[var(--space-2)] p-[var(--space-6)] text-center">
      {invalid ? (
        <>
          <Text component="h1" className="text-lg [font-weight:var(--font-semibold)]">
            {t("page.invalidTitle")}
          </Text>
          <Text component="p" className="text-sm text-[var(--text-secondary)]">
            {t("page.invalidBody")}
          </Text>
        </>
      ) : (
        <Text component="p" role="status" className="text-sm text-[var(--text-secondary)]">
          {t("page.starting")}
        </Text>
      )}
    </main>
  );
};
